import React, { useEffect, useState, useRef } from 'react';
import { RightArrow } from '../../../icons/right-arrow';
import { Eye } from '../../../icons/eye';
import { FormModal } from '../../../modal/form-modal';
import { Staleable, StatusLabel } from '../../../../motion';
import { ActionWithReason } from '../dashboard-shared-components/disabled-reason';
import {
  AddNewUserForm,
  AddNewUserFormContainer,
  SaveButton,
  ButtonTitle,
  InputContainer,
  Label,
  StyledUserInput,
  CancelButton,
  CheckboxesDropdown,
  FieldError,
  FormFieldset,
  PasswordVisibilityButton,
  ModeTabs,
  ModeTab,
  InviteModePanel,
  PropertySelectWrapper,
  PropertyCheckboxLabel,
  StyledSelectMenu,
  CheckRingContainer,
  CheckboxesContainerInSelectMenu,
  StyledSelectCheckboxes,
} from './dashboard-users.styled';
import { regexValidation } from '../../../../function-utils/regex-validation';
import { fetchAllItems } from '../../../requests/requests';
import { NewUser } from '../../../../interfaces/user-interfaces';
import { BuildingData } from '../../../../interfaces/building-interfaces';
import { CheckRing } from '../../../icons/check-ring';
import { useNavigate } from 'react-router-dom';
import { apiRoute } from '../../../../constants/api-constants';
import axios from 'axios';
import { InviteForm } from '../../../onboarding/invite/invite-form';
import {
  INVITABLE_ROLES,
  Role,
  ROLES,
  STAFF_ROLES,
  ROLES_SET_HERE,
  normalizeRole,
  outranks,
  roleLabel,
} from '../../../../auth/roles';
import { useAuth } from '../../../../context/auth-provider';
import { deskInPlan, roleLabelInPlan, rolesInPlan } from '../../../../utils/products';
import { receptionHotels } from '../../../../api/stays-hotel-client';
import toast from '../../../../utils/toast';

const newUserInitialState: NewUser = {
  full_name: '',
  email: '',
  password: '',
  property_ids: [],
  phone_number: '',
  role: ROLES.OWNER,
};

/**
 * WHICH ROLES THIS PERSON MAY CREATE.
 *
 * A property manager now holds `user:create` (backend permissions.js) so that
 * they can staff their own buildings, and the server refuses any target role
 * they do not outrank. Offering the full list regardless of who is looking
 * would therefore render two options that answer with a 403 — the failure is
 * indistinguishable from the feature being broken.
 *
 * OWNER IS NOT RUN THROUGH outranks(), matching the server: an owner may create
 * a co-owner, and an owner does not outrank an owner.
 *
 * UI ONLY. The server re-checks every create; this decides what is offered.
 */
const creatableRolesFor = (actorRole: string | null | undefined): Role[] =>
  normalizeRole(actorRole) === ROLES.OWNER
    ? [...ROLES_SET_HERE]
    : ROLES_SET_HERE.filter(target => outranks(actorRole, target));

interface DashboardAddNewUserProps {
  /**
   * WP-26. Called after a successful create, BEFORE navigating away.
   *
   * This form is a modal over the live list and the list's fiber survives the
   * navigation, so nothing refetches on its own — the person who just created a
   * user was returned to a list that did not contain them. Same shape the
   * portfolio form already uses (dashboard-portfolio-form.tsx onSaved).
   */
  onSaved?: () => void;
}

export const DashboardAddNewUser: React.FC<DashboardAddNewUserProps> = ({
  onSaved,
}) => {
  const { user } = useAuth();
  const actorRole = user?.role;
  // IS9: without Desk, a concierge ("Reception") only once there is a hotel.
  const [hotel, setHotel] = useState(false);
  const stayOnly = !deskInPlan(user);
  useEffect(() => {
    if (!stayOnly) return;
    let live = true;
    receptionHotels()
      .then(r => {
        if (live) setHotel(r.items.length > 0);
      })
      .catch(() => undefined);

    return () => {
      live = false;
    };
  }, [stayOnly]);
  // Without Lobbero Desk there are no concierges (Independent Stays IS1), but
  // a hotel's reception is one (IS9).
  const creatableRoles = rolesInPlan(creatableRolesFor(actorRole), user, { hotel });
  // The role the form opens on. `newUserInitialState` names owner, which is the
  // right default for an owner and an instant 403 for anybody else.
  const defaultRole = creatableRoles[0] || ROLES.CONCIERGE;
  // Staff only. A resident is added on the Residents screen, which places them
  // in a unit — an invitation minted here would carry no occupancy.
  const invitableStaffRoles = rolesInPlan(
    // IS5: a co-host is invited on Stays → Team (the invitation names listings).
    (INVITABLE_ROLES[normalizeRole(actorRole) || ROLES.CONCIERGE] || []).filter(role =>
      STAFF_ROLES.includes(role) && ROLES_SET_HERE.includes(role)
    ),
    user,
    { hotel }
  );

  const [newUserData, setNewUserData] = useState<NewUser>(() => ({
    ...newUserInitialState,
    role: defaultRole,
  }));
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const submitRef = useRef(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  // ── THE SECOND PASSWORD FIELD ─────────────────────────────────────────────
  // This form sets a password FOR SOMEBODY ELSE, and that person is not there
  // to try it. A typo in the single field shipped a working account nobody
  // could sign in to, and the only way out was an owner resetting a password
  // they had also never seen. It is kept OUT of `newUserData` deliberately:
  // that object is serialised straight into the request body, and a confirm
  // field is a client-side check, never a field the server should receive.
  const [passwordConfirm, setPasswordConfirm] = useState<string>('');
  const [invalidFieldsInputArray, setInvalidFieldsInputArray] = useState<
    string[]
  >([]);
  // Counts refused submits, so a field error that is already showing shakes
  // again on the next attempt instead of sitting still.
  const [attempt, setAttempt] = useState(0);
  const [properties, setProperties] = useState<BuildingData[]>([]);
  const [isPropertiesCheckboxesOpened, setIsPropertiesCheckboxesOpened] =
    useState<boolean>(false);
  const checkboxesContainerRef = useRef<HTMLDivElement | null>(null);
  const [existingEmailAddress, setExistingEmailAddress] =
    useState<boolean>(false);
  const [mode, setMode] = useState<'invite' | 'password'>('invite');

  const navigate = useNavigate();

  const getPropertiesfromDB = async () => {
    const response = await fetchAllItems('dashboard/buildings');

    setProperties(response as BuildingData[]);
  };

  useEffect(() => {
    getPropertiesfromDB();
  }, []);

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  const validateNewUserData = (data: any) => {
    // PHONE IS NOT HERE ON PURPOSE. The server requires only email and
    // password (users.js), so requiring a phone here refused a save the
    // backend would have accepted — and the box was showing a dial code it
    // had never put into state, so the field looked filled while it was not.
    const managerFields: string[] = [
      'full_name',
      'email',
      'password',
      'property_ids',
      'role',
    ];

    const emptyFields: string[] = [];

    managerFields.forEach(field => {
      if (data[field].length === 0) {
        emptyFields.push(field);
      }

      if (field === 'password' && data.password.length < 8) {
        emptyFields.push('password');
      }
    });

    const emailValidation = regexValidation('email', newUserData.email);

    if (!emailValidation.isValidEmail) {
      emptyFields.push('email');
    }

    // A MISMATCH IS ITS OWN ERROR, not "password invalid". The two fields are
    // both eight characters and both fine on their own; what is wrong is that
    // they disagree, and saying so is the difference between fixing it in one
    // try and retyping both.
    if (data.password.length >= 8 && passwordConfirm !== data.password) {
      emptyFields.push('password_confirm');
    }

    return emptyFields;
  };

  /* WP-32 / MR-22 — WHY THE SAVE BUTTON IS OFF, IN WORDS.
   *
   * The CTA was `disabled={isLoading}` — in-flight state and nothing else — so an
   * empty form offered a live Save and pressing it was the only way to discover
   * it was not ready. The master report downgraded this to Low on the strength of
   * R3, but R3 tested the PASSWORD path only and explicitly excluded invitations;
   * the plan restored it to Medium and the code agrees.
   *
   * THE GATE REUSES `validateNewUserData` RATHER THAN RESTATING IT. That function
   * is what refuses the submit, so deriving the button's state from anything else
   * would let the two disagree — a button disabled for a reason the form never
   * shows, or enabled into a refusal. One rule, one definition.
   *
   * AND IT SAYS WHY. A disabled button with no reason is a broken button
   * (disabled-reason.tsx, WP-43); replacing "enabled, then refused" with
   * "disabled, and silent" would be the same defect in new clothes. */
  const FIELD_WORDS: { [key: string]: string } = {
    full_name: 'a name',
    email: 'a valid email address',
    password: 'a password of at least 8 characters',
    password_confirm: 'two passwords that match',
    property_ids: 'at least one building',
    role: 'a role',
  };

  const outstandingReason = (): string | null => {
    // De-duplicated: validateNewUserData pushes 'password' twice for a short
    // one — once for empty, once for under eight — and "a password of at least
    // 8 characters and a password of at least 8 characters" is not a sentence.
    const missing = [...new Set(validateNewUserData(newUserData))];
    const parts = missing.map(field => FIELD_WORDS[field]).filter(Boolean);
    if (!parts.length) return null;

    const list =
      parts.length === 1
        ? parts[0]
        : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;

    return `Add ${list} to save.`;
  };

  const handleInputChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, value } = event.target;

    if (name === 'role') {
      // Handle changes for role input
      setNewUserData({ ...newUserData, [name]: value });
    } else if (name === 'phone_number') {
      // Validate and format phone number. An empty box stays EMPTY: phone is
      // optional, and a bare '+' would be the same fabricated value this form
      // has just stopped inventing — it would also make clearing a saved
      // number impossible.
      const numericValue = value.replace(/\D/g, '');
      setNewUserData({
        ...newUserData,
        [name]: numericValue ? '+' + numericValue : '',
      });
    } else if (name === 'password') {
      // Trim password input
      const trimmedPassword = value.trim();
      setNewUserData({ ...newUserData, [name]: trimmedPassword });
    } else {
      // Handle all other inputs
      setNewUserData({ ...newUserData, [name]: value });
    }
  };

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async e => {
    e.preventDefault();

    if (submitRef.current) {
      return;
    }

    const invalidFields = validateNewUserData(newUserData);
    setInvalidFieldsInputArray(invalidFields);

    if (invalidFields.length > 0) {
      setAttempt(count => count + 1);

      return;
    }

    submitRef.current = true;
    setIsLoading(true);
    try {
      const formData = new FormData();

      formData.append('full_name', newUserData.full_name);
      formData.append('email', newUserData.email);
      formData.append('password', newUserData.password);
      formData.append('property_ids', JSON.stringify(newUserData.property_ids));
      formData.append('phone_number', newUserData.phone_number);
      formData.append('role', newUserData.role);

      await axios.post(`${apiRoute}/dashboard/users/add-new-user`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setExistingEmailAddress(false);
      setNewUserData({ ...newUserInitialState, role: defaultRole });
      setPasswordConfirm('');
      setInvalidFieldsInputArray([]);
      toast.success(
        newUserData.full_name
          ? `${newUserData.full_name} added`
          : 'User added'
      );
      onSaved?.();
      navigate('/dashboard/users');
    } catch (error) {
      const message = axios.isAxiosError<{ message?: string }>(error)
        ? error.response?.data?.message
        : undefined;

      if (message === 'This email address already exists') {
        setExistingEmailAddress(true);
        setAttempt(count => count + 1);
      } else {
        toast.error(message || 'Could not add the user. Please try again.');
      }
    } finally {
      submitRef.current = false;
      setIsLoading(false);
    }
  };

  const showCheckboxes = () => {
    setIsPropertiesCheckboxesOpened(!isPropertiesCheckboxesOpened);
  };

  // Closing IS cancelling. Every exit — Cancel, the ×, Escape, the scrim —
  // comes through here, so there is one route back and one reset.
  const clearData = () => {
    setNewUserData({ ...newUserInitialState, role: defaultRole });
    setNewUserData(prevState => ({
      ...prevState,
      property_ids: [],
    }));
    setPasswordConfirm('');
    setInvalidFieldsInputArray([]);
    navigate('/dashboard/users');
  };

  useEffect(() => {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    function handleClickOutside(event: any) {
      if (
        checkboxesContainerRef.current &&
        !checkboxesContainerRef.current.contains(event.target)
      ) {
        // Clicked outside the container, so close it
        setIsPropertiesCheckboxesOpened(false);
      }
    }

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleCheckboxChange = (propertyId: string, isChecked: boolean) => {
    if (newUserData.role === ROLES.CONCIERGE) {
      if (isChecked) {
        setNewUserData(newUserData => ({
          ...newUserData,
          property_ids: [Number(propertyId)],
        }));
      } else {
        setNewUserData(newUserData => ({
          ...newUserData,
          property_ids: [
            ...newUserData.property_ids.filter(
              item => item !== Number(propertyId)
            ),
          ],
        }));
      }
    } else {
      if (isChecked) {
        setNewUserData(newUserData => ({
          ...newUserData,
          property_ids: [...newUserData.property_ids, Number(propertyId)],
        }));
      } else {
        setNewUserData(newUserData => ({
          ...newUserData,
          property_ids: [
            ...newUserData.property_ids.filter(
              item => item !== Number(propertyId)
            ),
          ],
        }));
      }
    }
  };

  /**
   * CHANGING THE ROLE NARROWS THE BUILDINGS. IT DOES NOT WIPE THE FORM.
   *
   * This used to be `setNewUserData({ ...newUserInitialState, role })` plus
   * `setPasswordConfirm('')` — so touching the Role select after filling the
   * form destroyed the name, the email, both password fields and the building
   * selection, with no warning and no undo. Role is the SECOND control on the
   * form; going back to correct it is an ordinary thing to do.
   *
   * The only thing role actually governs here is cardinality: a concierge works
   * ONE building (see handleCheckboxChange), so switching to that role trims a
   * multi-building selection down to the first rather than discarding it. Every
   * other field is orthogonal to the role and is left exactly as typed.
   */
  useEffect(() => {
    if (newUserData.role !== ROLES.CONCIERGE) return;

    setNewUserData(current =>
      current.property_ids.length > 1
        ? { ...current, property_ids: current.property_ids.slice(0, 1) }
        : current
    );
  }, [newUserData.role]);

  return (
    <FormModal
      /* Anything typed beyond the role the form opened on counts. Role alone
         does not: it has a default, so a user who opens the form and closes it
         again has lost nothing and must not be interrogated about it. */
      isDirty={
        JSON.stringify({ ...newUserData, role: defaultRole }) !==
          JSON.stringify({ ...newUserInitialState, role: defaultRole }) ||
        passwordConfirm.length > 0
      }
      discardMessage="This person has not been created yet. Close and lose what you have entered?"
      title="Add new user"
      subtitle={
        mode === 'invite'
          ? 'Send an invitation and let them set their own password.'
          : 'Set a password for them now.'
      }
      wide
      onClose={clearData}
      footer={
        // The invite panel carries its own send button; a second, disabled one
        // at the foot of the card would be a control that never applies.
        mode === 'password' ? (
          <>
            <CancelButton
              type="button"
              disabled={isLoading}
              onClick={() => clearData()}
            >
              <ButtonTitle>Cancel</ButtonTitle>
            </CancelButton>
            <ActionWithReason reason={isLoading ? null : outstandingReason()}>
              {describedBy => (
                <SaveButton
                  type="submit"
                  form="add-new-user-password-form"
                  disabled={isLoading || Boolean(outstandingReason())}
                  aria-describedby={describedBy}
                >
                  <StatusLabel
                    state={isLoading ? 'busy' : 'idle'}
                    labels={{
                      idle: (
                        <>
                          <ButtonTitle>Save</ButtonTitle>
                          <RightArrow />
                        </>
                      ),
                      busy: <ButtonTitle>Saving…</ButtonTitle>,
                    }}
                  />
                </SaveButton>
              )}
            </ActionWithReason>
          </>
        ) : null
      }
    >
      <ModeTabs aria-label="User creation method">
        <ModeTab
          type="button"
          $active={mode === 'invite'}
          aria-pressed={mode === 'invite'}
          onClick={() => setMode('invite')}
        >
          Send invite
        </ModeTab>
        <ModeTab
          type="button"
          $active={mode === 'password'}
          aria-pressed={mode === 'password'}
          onClick={() => setMode('password')}
        >
          Set a password
        </ModeTab>
      </ModeTabs>

      {mode === 'invite' && (
        <InviteModePanel>
          <InviteForm
            allowedRoles={invitableStaffRoles}
            buildings={properties.map(p => ({
              id: Number(p.id),
              name: p.property_name,
            }))}
          />
        </InviteModePanel>
      )}

      {mode === 'password' && (
        <>
          <AddNewUserFormContainer>
            {/* SAVING KEEPS THE FORM ON SCREEN. The absolute spinner that
                covered these fields is gone: in flight, the form dims
                (Staleable), is inert (the fieldset is disabled, aria-busy says
                so) and the Save button carries the pending state at a stable
                width. */}
            <AddNewUserForm
              id="add-new-user-password-form"
              aria-busy={isLoading || undefined}
              onSubmit={onSubmit}
            >
              <Staleable stale={isLoading}>
              <FormFieldset disabled={isLoading}>
              <InputContainer isRoleField={true}>
                <CheckRingContainer>
                  <CheckRing />
                </CheckRingContainer>
                <StyledSelectMenu
                  aria-label="Role"
                  name="role"
                  value={newUserData.role}
                  onChange={handleInputChange}
                  isRoleField={true}
                >
                  {/* Only what this person may actually create. A manager sees
                      Concierge alone; an owner sees all three, co-owner
                      included. */}
                  {creatableRoles.map(role => (
                    <option key={role} value={role}>
                      {roleLabelInPlan(role, user, roleLabel(role))}
                    </option>
                  ))}
                </StyledSelectMenu>
              </InputContainer>
              {/* No avatar upload here. `users.user_image` has exactly one
                  writer — PATCH /profile, i.e. the person themselves — and the
                  admin routes ignore an attached file entirely, so this control
                  took a photo and dropped it. An owner editing a colleague's
                  record has no business replacing that person's avatar either. */}
              <InputContainer>
                <Label htmlFor="add-user-name">User name</Label>
                <StyledUserInput
                  id="add-user-name"
                  type="text"
                  name="full_name"
                  value={newUserData.full_name}
                  placeholder={'Enter first and last name'}
                  onChange={handleInputChange}
                />
              </InputContainer>
              <FieldError
                show={invalidFieldsInputArray.includes('full_name')}
                attempt={attempt}
              >
                Please enter first and last name*
              </FieldError>
              <InputContainer>
                <Label htmlFor="add-user-email">Email</Label>
                <StyledUserInput
                  id="add-user-email"
                  type="email"
                  name="email"
                  value={newUserData.email}
                  placeholder={'Enter email address'}
                  onChange={handleInputChange}
                />
              </InputContainer>
              <FieldError
                show={invalidFieldsInputArray.includes('email')}
                attempt={attempt}
              >
                Please enter a valid email address*
              </FieldError>
              <FieldError show={existingEmailAddress} attempt={attempt}>
                This email address is already taken. Please try another.
              </FieldError>
              <InputContainer isPasswordInput={true}>
                <Label htmlFor="add-user-password">Password</Label>
                <StyledUserInput
                  id="add-user-password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={newUserData.password}
                  placeholder={'Enter password'}
                  onChange={handleInputChange}
                  hasTrailingAction
                />
                <PasswordVisibilityButton
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <Eye slashed={!showPassword} />
                </PasswordVisibilityButton>
              </InputContainer>
              <FieldError
                show={invalidFieldsInputArray.includes('password')}
                attempt={attempt}
              >
                Your password must be at least 8 characters*
              </FieldError>
              {/* ONE VISIBILITY TOGGLE FOR BOTH FIELDS. Two would let somebody
                  reveal one and not the other, which is the state where a
                  mismatch is hardest to see — and the whole point of the second
                  field is to catch a typo the writer cannot see. */}
              <InputContainer isPasswordInput={true}>
                <Label htmlFor="add-user-password-confirm">
                  Repeat password
                </Label>
                <StyledUserInput
                  id="add-user-password-confirm"
                  type={showPassword ? 'text' : 'password'}
                  name="password_confirm"
                  value={passwordConfirm}
                  placeholder={'Enter the same password again'}
                  onChange={event =>
                    setPasswordConfirm(event.target.value.trim())
                  }
                  hasTrailingAction
                />
                <PasswordVisibilityButton
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <Eye slashed={!showPassword} />
                </PasswordVisibilityButton>
              </InputContainer>
              <FieldError
                show={invalidFieldsInputArray.includes('password_confirm')}
                attempt={attempt}
              >
                The two passwords do not match*
              </FieldError>
              {/* ONE FIELD, NO DIAL-CODE SELECT. The select never seeded
                  itself from the saved number, so it read "USA" beside a +44
                  number, and changing it BLANKED the number before applying a
                  prefix — a stray click or a mouse-wheel over it destroyed
                  what was there. The dial code is part of the number the
                  person types, exactly as on account-profile.tsx. */}
              <InputContainer>
                <Label htmlFor="add-user-phone">Phone</Label>
                <StyledUserInput
                  id="add-user-phone"
                  type="tel"
                  name="phone_number"
                  autoComplete="tel"
                  value={newUserData.phone_number || ''}
                  placeholder={'Enter phone number (optional)'}
                  onChange={handleInputChange}
                />
              </InputContainer>
              <InputContainer>
                <Label htmlFor="add-user-properties">Property selection</Label>

                <PropertySelectWrapper ref={checkboxesContainerRef}>
                  <StyledSelectCheckboxes
                    id="add-user-properties"
                    type="button"
                    aria-haspopup="true"
                    aria-expanded={isPropertiesCheckboxesOpened}
                    aria-controls="property-checkboxes"
                    onClick={showCheckboxes}
                  >
                    {newUserData.property_ids.length > 0
                      ? `${
                          properties.filter(
                            prop =>
                              Number(prop.id) === newUserData.property_ids[0]
                          )[0].property_name
                        },${
                          properties.filter(
                            prop =>
                              Number(prop.id) === newUserData.property_ids[0]
                          )[0].address
                        }`
                      : 'Property selection'}
                  </StyledSelectCheckboxes>
                  {/* Unfolds under the button (Collapse) and folds away on
                      dismissal; aria-controls still points at the id. */}
                  <CheckboxesDropdown
                    open={isPropertiesCheckboxesOpened}
                    id="property-checkboxes"
                  >
                    <CheckboxesContainerInSelectMenu
                      role="group"
                      aria-label="Properties"
                    >
                      {properties.map(property => (
                        <PropertyCheckboxLabel key={property.id}>
                          <input
                            name="property_ids"
                            type="checkbox"
                            value={property.id}
                            checked={newUserData.property_ids.includes(
                              Number(property.id) as never
                            )}
                            onChange={e =>
                              handleCheckboxChange(
                                property.id,
                                e.target.checked
                              )
                            }
                          />
                          {property.property_name}, {property.address}
                        </PropertyCheckboxLabel>
                      ))}
                    </CheckboxesContainerInSelectMenu>
                  </CheckboxesDropdown>
                </PropertySelectWrapper>
              </InputContainer>
              <FieldError
                show={invalidFieldsInputArray.includes('property_ids')}
                attempt={attempt}
              >
                Please select at least one property*
              </FieldError>
              </FormFieldset>
              </Staleable>
            </AddNewUserForm>
          </AddNewUserFormContainer>
        </>
      )}
    </FormModal>
  );
};
